import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { environment } from 'environments/environment';
import { FileCaptureService } from './file-capture.service';
import {
  AttachmentEntity,
  AttachmentList,
  AttachmentResponse,
  BulkUploadAttachmentResponse
} from './interfaces';

@Injectable({
  providedIn: 'root'
})
export class BulkAttachmentUploadService {
  private baseUrl = environment.baseUri;

  constructor(
    private http: HttpClient,
    private fileCaptureService: FileCaptureService
  ) { }

  private normalizeAttachmentResponse(data: any): AttachmentResponse {
    if (!data || typeof data !== 'object') {
      return {
        fileName: '',
        caseActivityKey: 0,
        attachmentKey: 0,
        isSuccess: false,
        errorMessage: ''
      };
    }

    return {
      fileName: data.fileName ?? data.FileName ?? '',
      caseActivityKey: Number(data.caseActivityKey ?? data.CaseActivityKey ?? 0),
      attachmentKey: Number(data.attachmentKey ?? data.AttachmentKey ?? 0),
      isSuccess: Boolean(data.isSuccess ?? data.IsSuccess ?? false),
      errorMessage: data.errorMessage ?? data.ErrorMessage ?? ''
    };
  }

  private normalizeAttachmentResponseList(data: any): AttachmentResponse[] {
    if (Array.isArray(data)) {
      return data.map((item: any): AttachmentResponse => this.normalizeAttachmentResponse(item));
    }

    if (Array.isArray(data?.attachmentResponseList)) {
      return data.attachmentResponseList.map((item: any): AttachmentResponse => this.normalizeAttachmentResponse(item));
    }

    return [];
  }

  private normalizeBulkUploadAttachmentResponse(data: any): BulkUploadAttachmentResponse {
    return {
      attachmentResponseList: this.normalizeAttachmentResponseList(data),
      isSuccess: data?.isSuccess ?? data?.IsSuccess ?? false,
      errorMessage: data?.errorMessage ?? data?.ErrorMessage ?? ''
    };
  }

  public addAttachmentsToDBBulk(data: AttachmentEntity[] | null, referenceId: string, caseActivityKey: number = 0): Observable<BulkUploadAttachmentResponse> {
    const sessionId = this.fileCaptureService.getActiveReferenceSessionId();
    const params = new HttpParams()
      .set('sessionId', sessionId)
      .set('referenceId', referenceId || '')
      .set('caseActivityKey', String(caseActivityKey || 0));
    const timeZoneName = this.fileCaptureService.getUsersTimeZone();

    const payload: AttachmentEntity[] = (data || []).map((item: AttachmentEntity) => ({
      ...item,
      timeZoneName
    }));

    const requestBody: AttachmentList = {
      attachmentList: payload
    };

    console.log('[BulkAttachmentUpload][AddAttachmentsToDBBulk] request', {
      referenceId,
      caseActivityKey,
      fileCount: payload.length,
      fileNames: payload.map((item: AttachmentEntity) => item.fileName),
      sessionId
    });

    return this.http.post<any>(this.baseUrl + 'FileCapture/AddAttachmentsToDBBulk', requestBody, { params }).pipe(
      map((response: any) => this.normalizeBulkUploadAttachmentResponse(response)),
      tap({
        next: (response: BulkUploadAttachmentResponse) => {
          console.log('[BulkAttachmentUpload][AddAttachmentsToDBBulk] response', {
            referenceId,
            caseActivityKey,
            fileCount: payload.length,
            sessionId,
            response
          });
        },
        error: (error: any) => {
          console.log('[BulkAttachmentUpload][AddAttachmentsToDBBulk] error', {
            referenceId,
            caseActivityKey,
            fileCount: payload.length,
            sessionId,
            status: error?.status,
            statusText: error?.statusText,
            message: error?.message,
            error: error?.error
          });
        }
      })
    );
  }
}
